import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { User, Loader2, Check, Handshake } from 'lucide-react';
import { API } from '../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';

const formatCPF = (v) => {
  const d = v.replace(/\D/g, '').slice(0, 11);
  return d
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1-$2');
};
const formatPhone = (v) => {
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 10) return d.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{4})(\d)/, '$1-$2');
  return d.replace(/^(\d{2})(\d)/, '($1) $2').replace(/(\d{5})(\d)/, '$1-$2');
};

const emptyForm = {
  name: '',
  cpf: '',
  birth_date: '',
  sex: '',
  cellphone: '',
  email: '',
  city: '',
  state: '',
  password: '',
  confirmPassword: '',
  is_affiliated: false,
  affiliation_registration_number: '',
  affiliation_name: '',
};

const PersonalRegistrationDialog = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirmPassword) {
      toast.error('As senhas não conferem');
      return;
    }
    if (!termsAccepted) {
      toast.error('Você precisa aceitar os Termos de Uso');
      return;
    }
    if (form.is_affiliated) {
      if (!form.affiliation_registration_number.trim()) {
        toast.error('Informe o número de matrícula na associação');
        return;
      }
      if (!form.affiliation_name.trim()) {
        toast.error('Informe o nome da associação/empresa conveniada');
        return;
      }
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/register/personal`, {
        name: form.name,
        cpf: form.cpf,
        birth_date: form.birth_date,
        sex: form.sex,
        cellphone: form.cellphone,
        email: form.email,
        city: form.city,
        state: form.state,
        password: form.password,
        terms_accepted: true,
        is_affiliated: form.is_affiliated,
        affiliation_registration_number: form.is_affiliated ? form.affiliation_registration_number.trim() : null,
        affiliation_name: form.is_affiliated ? form.affiliation_name.trim() : null,
      });
      // Store session_token and go straight to student dashboard
      if (res.data?.session_token) {
        localStorage.setItem('session_token', res.data.session_token);
      }
      setSuccess(true);
      toast.success('Cadastro realizado! Escolha um plano para começar.');
      // Brief delay so user reads the success message, then redirect to plans
      setTimeout(() => {
        onClose();
        navigate('/plans/student');
      }, 1200);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar cadastro');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setSuccess(false);
    setForm(emptyForm);
    setTermsAccepted(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-lg p-0"
        style={{ maxHeight: '90dvh', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}
        data-testid="personal-reg-dialog"
      >
        <div className="px-6 py-6">
          {success ? (
            <div className="text-center py-4" data-testid="personal-reg-success">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-emerald-600" />
              </div>
              <DialogTitle className="text-xl mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Cadastro Realizado!
              </DialogTitle>
              <p className="text-sm text-slate-600 mb-2 leading-relaxed">
                Bem-vindo(a) ao Vital Performance!
                Você já pode escolher seu plano e começar a treinar.
              </p>
              <p className="text-xs text-slate-500 flex items-center justify-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" /> Redirecionando para os planos...
              </p>
            </div>
          ) : (
            <>
              <DialogHeader className="pb-3">
                <DialogTitle
                  className="flex items-center gap-2"
                  style={{ fontFamily: 'Chivo, sans-serif' }}
                >
                  <User className="w-5 h-5 text-emerald-600" /> Cadastro Pessoal
                </DialogTitle>
                <DialogDescription className="text-xs text-slate-500">
                  Preencha seus dados — o acesso é imediato após o cadastro
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                  <Label className="text-xs text-slate-600">Nome completo *</Label>
                  <Input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="preg-name"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-600">CPF *</Label>
                    <Input
                      required
                      value={form.cpf}
                      onChange={(e) => setForm({ ...form, cpf: formatCPF(e.target.value) })}
                      placeholder="000.000.000-00"
                      maxLength={14}
                      data-testid="preg-cpf"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">Data Nascimento *</Label>
                    <Input
                      required
                      type="date"
                      value={form.birth_date}
                      onChange={(e) => setForm({ ...form, birth_date: e.target.value })}
                      data-testid="preg-birth"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-600">Sexo *</Label>
                    <select
                      required
                      value={form.sex}
                      onChange={(e) => setForm({ ...form, sex: e.target.value })}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      data-testid="preg-sex"
                    >
                      <option value="">Selecione</option>
                      <option value="M">Masculino</option>
                      <option value="F">Feminino</option>
                      <option value="Outro">Outro</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">Celular *</Label>
                    <Input
                      required
                      value={form.cellphone}
                      onChange={(e) =>
                        setForm({ ...form, cellphone: formatPhone(e.target.value) })
                      }
                      placeholder="(00) 00000-0000"
                      data-testid="preg-phone"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-600">E-mail *</Label>
                  <Input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="seu@email.com"
                    data-testid="preg-email"
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2">
                    <Label className="text-xs text-slate-600">Cidade *</Label>
                    <Input
                      required
                      value={form.city}
                      onChange={(e) => setForm({ ...form, city: e.target.value })}
                      data-testid="preg-city"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">UF *</Label>
                    <Input
                      required
                      value={form.state}
                      onChange={(e) =>
                        setForm({ ...form, state: e.target.value.toUpperCase() })
                      }
                      maxLength={2}
                      data-testid="preg-state"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-600">Senha *</Label>
                    <Input
                      required
                      type="password"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      minLength={6}
                      data-testid="preg-password"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">Confirmar *</Label>
                    <Input
                      required
                      type="password"
                      value={form.confirmPassword}
                      onChange={(e) =>
                        setForm({ ...form, confirmPassword: e.target.value })
                      }
                      data-testid="preg-confirm-password"
                    />
                  </div>
                </div>

                {/* AFFILIATION BLOCK */}
                <div className="mt-2 rounded-xl border border-emerald-200 bg-emerald-50/40 p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Handshake className="w-4 h-4 text-emerald-600" />
                    <span className="text-xs font-semibold text-slate-800">
                      Faz parte de alguma associação conveniada?
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="is_affiliated"
                        checked={form.is_affiliated === true}
                        onChange={() => setForm({ ...form, is_affiliated: true })}
                        className="accent-emerald-600 w-4 h-4"
                        data-testid="preg-affiliated-yes"
                      />
                      <span className="text-sm text-slate-700">Sim</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="is_affiliated"
                        checked={form.is_affiliated === false}
                        onChange={() =>
                          setForm({
                            ...form,
                            is_affiliated: false,
                            affiliation_registration_number: '',
                            affiliation_name: '',
                          })
                        }
                        className="accent-emerald-600 w-4 h-4"
                        data-testid="preg-affiliated-no"
                      />
                      <span className="text-sm text-slate-700">Não</span>
                    </label>
                  </div>

                  {form.is_affiliated && (
                    <div className="mt-3 grid grid-cols-1 gap-3">
                      <div>
                        <Label className="text-xs text-slate-600">
                          Número de matrícula na associação *
                        </Label>
                        <Input
                          required
                          value={form.affiliation_registration_number}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              affiliation_registration_number: e.target.value,
                            })
                          }
                          placeholder="Ex.: 123456"
                          data-testid="preg-affiliation-number"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-600">
                          Nome da associação ou empresa conveniada *
                        </Label>
                        <Input
                          required
                          value={form.affiliation_name}
                          onChange={(e) =>
                            setForm({ ...form, affiliation_name: e.target.value })
                          }
                          placeholder="Ex.: SESC, OAB, Sindicato dos Bancários..."
                          data-testid="preg-affiliation-name"
                        />
                      </div>
                      <p className="text-[11px] text-slate-500 leading-snug">
                        Os dados serão utilizados exclusivamente para validação da parceria.
                      </p>
                    </div>
                  )}
                </div>

                <div className="border-t border-slate-100 pt-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <Checkbox
                      checked={termsAccepted}
                      onCheckedChange={setTermsAccepted}
                      data-testid="preg-terms-checkbox"
                      className="mt-0.5"
                    />
                    <span className="text-xs text-slate-600 leading-relaxed">
                      Li e aceito os{' '}
                      <a
                        href="/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-emerald-600 font-semibold underline"
                      >
                        Termos de Uso
                      </a>{' '}
                      e Política da Plataforma. *
                    </span>
                  </label>
                </div>

                <Button
                  type="submit"
                  disabled={submitting || !termsAccepted}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
                  data-testid="preg-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando...
                    </>
                  ) : (
                    'Enviar Cadastro para Análise'
                  )}
                </Button>
              </form>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PersonalRegistrationDialog;
